import React, { useEffect } from 'react';

import { type BookingRequest, useInvalidateBookingSummary } from '@/booking';
import { useUpdateBookingStatusCache } from '@/booking/hooks/use-booking-status.hook';
import { DomainError } from '@/errors';
import { useLocale, useTranslation } from '@/i18n';
import { trpc } from '@/trpc/client';
import { Button, TranslatedErrorMessage } from '@/ui/controls';
import { ConfirmationModal } from '@/ui/controls/ConfirmationModal.component';
import { Spacer } from '@/ui/spacing';
import { useTheme } from '@/ui/theme';
import { BodySmall } from '@/ui/typography';

import { useCancelBookingMutation } from '../hooks/use-cancel-booking-mutation.hook';

interface BookingCancellationModalProps {
  onRequestClose: () => void;
  isOpen: boolean;
  booking: BookingRequest;
}

export const BookingCancellationModal: React.FC<
  BookingCancellationModalProps
> = (props) => {
  const cancelMutation = useCancelBookingMutation();
  const { t } = useTranslation('bookingManagement');
  const locale = useLocale();
  const invalidateBookingSummary = useInvalidateBookingSummary(props.booking);
  const updateBookingStatusCache = useUpdateBookingStatusCache();
  const context = trpc.useContext();

  const {
    componentProperties: { bookingManagement },
  } = useTheme();

  useEffect(() => {
    // Reset on open
    if (!props.isOpen) {
      cancelMutation.reset();
    }
  }, [props.isOpen]);

  return (
    <ConfirmationModal
      isLoading={cancelMutation.isLoading}
      onRequestClose={props.onRequestClose}
      title={t('cancellation.modal.title')}
      isOpen={props.isOpen}
      css={{
        ...bookingManagement?.bookingCard?.cancellationModal,
        h6: { span: bookingManagement?.bookingCard?.cancellationModal?.title },
        ['@media all and (max-width: 730px)']: {
          ...bookingManagement?.bookingCard?.cancellationModal?.mobile,
          h6: {
            span: bookingManagement?.bookingCard?.cancellationModal?.mobile
              ?.title,
          },
        },
      }}
    >
      <BodySmall
        css={{
          ...bookingManagement?.bookingCard?.cancellationModal?.body,
          '@media all and (max-width: 730px)':
            bookingManagement?.bookingCard?.cancellationModal?.body?.mobile,
        }}
      >
        {t('cancellation.modal.body')}
      </BodySmall>
      <Spacer s="xl" />
      <Button
        onClick={() =>
          cancelMutation.mutate(
            {
              bookingId: props.booking.bookingId,
              hotelReferenceId: props.booking.hotelSlug, // @todo - rename this property
              locale: locale.baseName,
            },
            {
              onSuccess: async ({ status }) => {
                updateBookingStatusCache(props.booking.bookingId, status);

                await Promise.all([
                  invalidateBookingSummary(),
                  context.bookingManagement.upcomingBookings.invalidate(),
                ]);

                props.onRequestClose();
              },
            }
          )
        }
        isLoading={cancelMutation.isLoading}
        variant={
          bookingManagement?.bookingCard?.cancellationModal?.confirmButton
            ?.variant ?? 'destructive'
        }
        css={bookingManagement?.bookingCard?.cancellationModal?.confirmButton}
      >
        {t('cancellation.modal.cancelButtonLabel')}
      </Button>
      <Spacer s="xxs" />
      <Button
        onClick={props.onRequestClose}
        variant="secondary"
        {...bookingManagement?.bookingCard?.cancellationModal?.cancelButton}
      >
        {t('cancellation.modal.noThanksButtonLabel')}
      </Button>
      {cancelMutation.error && !cancelMutation.isLoading && (
        <>
          <Spacer s="xs" />
          <TranslatedErrorMessage
            dictionaryKey={
              // todo: clean this up! bit messy - temporary until we implement the cancel flow
              cancelMutation.error.data?.domainError
                ? new DomainError(cancelMutation.error.data?.domainError)
                    .messageTranslationKey
                : 'generic'
            }
          />
        </>
      )}
    </ConfirmationModal>
  );
};
